import { api, type APIKey, type KeyStatus, type SortOrder, type TaskStatus } from "./client";

export type { APIKey, KeyStatus, TaskStatus } from "./client";

// --- supplier 域类型(doc/046 §6.1 /api/supplier/*,沿用 /api/v1 前缀) ---
// 供应商侧 DTO 已由后端脱敏:只含 target_code 代号与分类后的 error,绝无真实站点信息。

export interface SupplierTarget {
  target_code: string;
  site_type: "relay" | "push";
}

// SupplierTaskBrief — 供应商可见的分发任务摘要(target_code = 脱敏代号快照;
// error_message 已分类,如「目标连接失败」)。
export interface SupplierTaskBrief {
  id: number;
  key_id: number;
  target_code: string;
  site_type: "relay" | "push";
  status: TaskStatus;
  error_message?: string;
  created_at: string;
  completed_at?: string | null;
}

export interface SupplierDashboard {
  keys_count: number;
  targets_count: number;
  tasks_total: number;
  tasks_by_status: Record<string, number>;
  recent: SupplierTaskBrief[];
}

export interface SupplierDistributeFailedItem {
  key_id?: number;
  target_code?: string;
  error: string;
}

export interface SupplierDistributeResult {
  operation_id: string;
  success: SupplierTaskBrief[];
  failed: SupplierDistributeFailedItem[];
  success_count: number;
  failed_count: number;
}

export interface SupplierTemplate {
  id: number;
  code: string;
  name: string;
  protocol_type: number;
  models: string;
  model_mapping: string;
  target_codes: string[];
}

export interface SupplierPublishBody {
  mode: "single" | "multi" | "batch";
  name: string;
  keys: string[];
  base_url?: string;
  template_id: number;
  models?: string | null;
  model_mapping?: string | null;
  target_codes: string[];
  remark?: string;
}

export interface SupplierPublishResult {
  operation_id: string;
  keys: APIKey[];
  success: SupplierTaskBrief[];
  failed: SupplierDistributeFailedItem[];
  created_keys: number;
  success_count: number;
  failed_count: number;
  auto_added_models?: string[];
}

export interface SupplierTargetUsage {
  target_code: string;
  site_type: "relay" | "push";
  total: number | null;
  coverage: string;
  status: string;
  last_success_at?: string | null;
}

export interface SupplierKeyUsage {
  key_id: number;
  key_name: string;
  key_hint: string;
  models: string;
  status: KeyStatus;
  created_at: string;
  total: number | null;
  display_unit: string;
  coverage: string;
  latest_sampled_at?: string | null;
  targets: SupplierTargetUsage[];
}

export type SupplierRequestStatus = "processing" | "success" | "partial_success" | "failed";

export interface SupplierRequestSummary {
  id: number;
  request_no: string;
  template_name?: string;
  submitted_key_count: number;
  accepted_key_count: number;
  status: SupplierRequestStatus;
  submitted_at: string;
  completed_at?: string | null;
}

export interface SupplierRequestTaskResult {
  task_id: number;
  target_code: string;
  site_type: "relay" | "push";
  status: TaskStatus | "staged";
  safe_error_code?: string;
  dispatched_at?: string | null;
  completed_at?: string | null;
}

export interface SupplierRequestItemResult {
  input_index: number;
  key_id?: number | null;
  key_name?: string;
  status: string;
  safe_error_code?: string;
  completed_at?: string | null;
  tasks: SupplierRequestTaskResult[];
}

export interface SupplierRequestDetail {
  request: SupplierRequestSummary;
  items: SupplierRequestItemResult[];
}

// SupplierUsageParams — GET /supplier/usage/summaries 的筛选/排序/分页契约(doc/054
// §1.1)。无参调用 = 后端默认(key_id desc、page_size 20);对账卡全量走 page_size 1000。
export interface SupplierUsageParams {
  key_id?: number[];
  q?: string;
  status?: KeyStatus;
  coverage?: "complete" | "partial" | "none";
  created_after?: string;
  page?: number;
  page_size?: number;
  sort?: string;
  order?: SortOrder;
}

export interface SupplierKeyCreateBody {
  name: string;
  key: string;
  base_url?: string;
  models?: string;
  unit_price?: number;
  protocol_type?: number;
  expires_at?: string;
  status?: KeyStatus;
  remark?: string;
}

export interface SupplierListParams {
  q?: string;
  status?: KeyStatus;
  page?: number;
  page_size?: number;
}

export const supplierApi = {
  dashboard: () => api.get<SupplierDashboard>("/supplier/dashboard"),

  listKeys: (params?: SupplierListParams) => {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.status) q.set("status", params.status);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    const qs = q.toString();
    return api.get<{ items: APIKey[]; total: number; page: number; page_size: number }>(
      `/supplier/keys${qs ? `?${qs}` : ""}`
    );
  },

  createKey: (body: SupplierKeyCreateBody) =>
    api.post<APIKey>("/supplier/keys", body),

  deleteKey: (id: number) =>
    api.del<{ deleted: number; soft?: boolean }>(`/supplier/keys/${id}`),

  listTargets: () => api.get<{ items: SupplierTarget[] }>("/supplier/targets"),

  // 自主发布(doc/051):模板只含协议/模型/映射与绑定到的脱敏目标代号。
  listTemplates: () => api.get<{ items: SupplierTemplate[] }>("/supplier/templates"),

  publish: (body: SupplierPublishBody) =>
    api.post<SupplierPublishResult>("/supplier/publish", body),

  usageSummaries: (params?: SupplierUsageParams) => {
    const q = new URLSearchParams();
    (params?.key_id ?? []).forEach((id) => q.append("key_id", String(id)));
    if (params?.q) q.set("q", params.q);
    if (params?.status) q.set("status", params.status);
    if (params?.coverage) q.set("coverage", params.coverage);
    if (params?.created_after) q.set("created_after", params.created_after);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    if (params?.sort) q.set("sort", params.sort);
    if (params?.order) q.set("order", params.order);
    const qs = q.toString();
    return api.get<{ items: SupplierKeyUsage[]; total: number; page: number; page_size: number }>(
      `/supplier/usage/summaries${qs ? `?${qs}` : ""}`
    );
  },

  refreshUsage: (key_ids: number[]) =>
    api.post<{ refreshed: number; failed: number }>("/supplier/usage/refresh", { key_ids }),

  // 分发:后端把 target_code 解析回真实站点,进入现有分发链路;响应 success/failed
  // 均已脱敏。单个目标不可用 → 置入 failed(目标不可用),不再产生任务行。
  distribute: (body: { key_ids: number[]; target_codes: string[] }) =>
    api.post<SupplierDistributeResult>("/supplier/distribute", body),

  listRecords: (params?: { status?: TaskStatus; page?: number; page_size?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    const qs = q.toString();
    return api.get<{ items: SupplierTaskBrief[]; total: number; page: number; page_size: number }>(
      `/supplier/records${qs ? `?${qs}` : ""}`
    );
  },

  listRequests: (params?: { status?: SupplierRequestStatus; page?: number; page_size?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    const query = q.toString();
    return api.get<{ items: SupplierRequestSummary[]; total: number; page: number; page_size: number }>(
      `/supplier/requests${query ? `?${query}` : ""}`
    );
  },

  getRequest: (id: number) => api.get<SupplierRequestDetail>(`/supplier/requests/${id}`),

  changePassword: (body: { current_password: string; new_password: string }) =>
    api.put<{ updated: boolean }>("/supplier/password", body),
};
